import { recordPerformance } from './speedmeister';

interface PerformanceEntry {
  step: string;
  timestamp: number;
  delta: number;
  totalElapsed: number;
}

class PerformanceDebugger {
  private entries: PerformanceEntry[] = [];
  private startTime: number;
  private lastTime: number;
  private pageType: string;

  constructor(pageType: string) {
    this.pageType = pageType;
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    this.log('PAGE_INIT', 'Page initialization started');
  }

  log(step: string, details?: string) {
    const now = performance.now();
    const delta = now - this.lastTime;
    const totalElapsed = now - this.startTime;

    const entry: PerformanceEntry = {
      step,
      timestamp: now,
      delta,
      totalElapsed
    };

    this.entries.push(entry);
    
    // Log to console with color coding
    const color = delta > 100 ? 'color: red' : delta > 50 ? 'color: orange' : 'color: green';
    console.log(
      `%c[${this.pageType}] ${step}${details ? ` - ${details}` : ''}: +${delta.toFixed(2)}ms (total: ${totalElapsed.toFixed(2)}ms)`,
      color
    );

    // Also record to SpeedMeister for visual overlay
    recordPerformance(this.pageType, step, delta, details);

    this.lastTime = now;
  }

  async measureAsync<T>(step: string, fn: () => Promise<T>): Promise<T> {
    this.log(`${step}_START`);
    try {
      const result = await fn();
      this.log(`${step}_END`);
      return result;
    } catch (error) {
      this.log(`${step}_ERROR`);
      throw error;
    }
  }

  measure<T>(step: string, fn: () => T): T {
    this.log(`${step}_START`);
    try {
      const result = fn();
      this.log(`${step}_END`);
      return result;
    } catch (error) {
      this.log(`${step}_ERROR`);
      throw error;
    }
  }

  getSummary() {
    const totalTime = performance.now() - this.startTime;
    const slowSteps = this.entries.filter(e => e.delta > 50);
    
    console.group(`🔍 ${this.pageType} Performance Summary`);
    console.log(`Total load time: ${totalTime.toFixed(2)}ms`);
    console.log(`Number of steps: ${this.entries.length}`);
    
    if (slowSteps.length > 0) {
      console.group('⚠️ Slow steps (>50ms):');
      slowSteps.forEach(step => {
        console.log(`${step.step}: ${step.delta.toFixed(2)}ms`);
      });
      console.groupEnd();
    }

    console.table(this.entries.map(e => ({
      Step: e.step,
      'Delta (ms)': e.delta.toFixed(2),
      'Total (ms)': e.totalElapsed.toFixed(2)
    })));
    
    console.groupEnd();
  }
}

// Export a singleton for each page type
const debuggers: Record<string, PerformanceDebugger> = {};

export function getDebugger(pageType: string): PerformanceDebugger {
  // Always create a fresh debugger for each page load
  // This prevents timing issues when navigating between pages
  debuggers[pageType] = new PerformanceDebugger(pageType);
  return debuggers[pageType];
}

export function resetDebugger(pageType: string) {
  debuggers[pageType] = new PerformanceDebugger(pageType);
  return debuggers[pageType];
}

// Export types for the hook file
export type { PerformanceDebugger };